import dayjs from 'dayjs'
import { lookupClientData } from './getClientDataFromApi';

/** ==============================================================================
 *  When we are parsing content to appear in the template, we often need to parse
 *  placeholder values . All parsing of template content happens here.
 * 
 *  This function requires:
 *  
 *  - The placeholder
 *  - the data obj which has the real data values (dataObj)
 *  - the global api data (apiData)
 * 
 *  Placeholders will usually be surrounded by double square brackets, like this:
 *      [[placeholder]]
 *  or single curly braces, like this:
 *    { placeholder } 
 * 
 *  Inside the placeholder we can also specify a modifier, like this:
 *   [[placeholder::modifier]]
 * 
 * ================================================================================ */

interface ParseContentProps {
  content:string
  clientDataObj:any 
  apiData:any
}

/** Parses content that may contain multiple placeholders */
export function parseContent({ content, clientDataObj, apiData }:ParseContentProps) {
  let report = ""
  const regex = /(\[\[\s*(.*?)\s*(?:::(.*?))?\s*\]\]|\{\s*(.*?)\s*(?:::(.*?))?\s*\})/g;

  // look up the placeholder in apiData and see if any format has been specified 
  // const format = apiData.find( (item:any) => item.placeholder === `[[${placeholder}]]` )?.format
  // console.log("format", format)

  // console.log(`content to evaluate: ${content}`)

  const result = content.replace(regex, (match, _, key1, modifier1, key2, modifier2) => {
    const key = key1 || key2;  // Get the main placeholder name
    const modifier = modifier1 || modifier2; // Get the modifier if present

    report += `Parsing the content has found this key: ${key} with modifier: ${modifier}`

    const lookupType = (key.startsWith("rp.") ||  key.startsWith("merchant.") ||  key.startsWith("merch.")) ? "path" : "placeholder"
    const apiDataLookup = apiData.find( (item:any) => item[lookupType] === key )

    const format = apiDataLookup?.format // Get the format if present

    let clientData
    try {
      const { result, report:apiReport } = lookupClientData({ clientDataObj, apiData, lookupType, lookupValue: key }) 
      report += apiReport 
      clientData = result
    } catch (error) {
      report += `Error getting value for key "${key}": ${error}`
    }

    report += `The following value was returned from the client feed: ${clientData}`
    
    if (!clientData) {
      report += `No value found for key "${key}".` // If no value is found, return the original placeholder 
      return ""
    }
    // a modifier overrides any default format specified in the apidata object.
    else if( clientData && modifier ) {
      try {
        clientData = applyModifier(clientData, modifier) // Apply the modifier if present
      } catch (error) {
        report += `Error applying modifier "${modifier}" to value "${clientData}": ${error}`
      }
    } else if( clientData && format ) {
      try {
        clientData = applyFormatter(clientData, format) // Apply the modifier if present
      } catch (error) { 
        report += `Error applying format "${format}" to clientData "${clientData}": ${error}`
      }
    }
    
    // console.log(`The value after applying modifier/format is: ${clientData}`)
    return clientData
  })

  // console.log(result) 
  return { result, report } 
}


export function parseContentGroup({ group, clientDataObj, apiData }:any) {
  let results = {}
  group.forEach( (contentObj:any) => {
    const { result, report } = parseContent({ content: contentObj.content, clientDataObj, apiData })
    results = {...results, [contentObj.key]: result}
  })
  return results
}


export function applyFormatter(value:any, format:string):any {
  if( !format ) return value
  const formatType = format.split("|")[0]
  const formatConfig = format.split("|")[1]

  // console.log(`applyFormatter: ${value}`)
  // console.log(`formatType: ${formatType}`)
  // console.log(`formatConfig: ${formatConfig}`)

  if( formatType === "date" ) {
    const dateFormat = formatConfig || "MMM DD, YYYY"
    value = dayjs(value).format(dateFormat)
  } else if (formatType === "number"){
    if( formatConfig === "float" ){
      value = parseFloat(value).toFixed(2)
    } else if( formatConfig === "int" ){
      value = parseInt(value)
    } else if( formatConfig === "usd" ){
      value = `$${parseFloat(value).toFixed(2)}`
    } else if( formatConfig === "euro" ){
      value = `€${parseFloat(value).toFixed(2)}`
    } else if( formatConfig === "percent" ){
      value = `${parseFloat(value).toFixed(2)}%`
    } else if( formatConfig === "capitalize" ){
      value = value.replace(/\b\w/g, (char:any) => char.toUpperCase())
    } else if( formatConfig === "lowercase" ){
      value = value.toLowerCase()
    } else {
      console.warn(`Unknown format config: ${formatConfig}`)
    }
  }
  return value
}


export function applyModifier(value:any, modifier:string):any {
  if( !modifier ) return value

  switch (modifier.toLowerCase()) {
    case "uppercase":
      value = value.toUpperCase();
      break;
    case "lowercase":
      value = value.toLowerCase();
      break;
    case "count":
      value = Array.isArray(value) ? value.length : "N/A";
      break;
    case "date":
      const dateVal = dayjs(value).format("MMM DD, YYYY");
      value = dateVal; 
      break;
    case "usd":
      value = `$${parseFloat(value).toFixed(2)}`;
      break;
    default:
      console.warn(`Unknown modifier: ${modifier}`);
  }

  return value;
}